import Color from 'color'
import { IndicationItem } from 'features/indicationTriage'
import { useGeckoLocale } from 'features/locale'
import Plot from 'features/plotly'
import range from 'lodash/range'
import { Layout, PlotData } from 'plotly.js'
import React, { FC, useCallback, useMemo, useState } from 'react'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { CriticalProfile } from './criticalProfileApi'

export interface CriticalProfileChartProps {
  cdf: GeckoComponentDataFrame | undefined
  profiles: CriticalProfile[]
  colors: Record<string, string>
  selection: IndicationItem['selection']
  showAreaOnHover?: boolean
  segmentHoverString?: (segmentIdx: number) => string
}

const PLOTLY_FG_COLOR = CantileverTheme.colors.grayscale[100]
const PLOTLY_FONT = {
  color: PLOTLY_FG_COLOR,
}

function deriveHoverColors(primary: string): [string, string] {
  const primaryColor = new Color(primary)
  const hoverLineColor = primaryColor
  const hoverFillColor = primaryColor.a(0.2)
  return [hoverLineColor.hex(), hoverFillColor.hex()]
}

const COLOR_TRANSPARENT = 'rgba(0,100,80,0.2)'

function splitTraceAndAddHover(
  data: Partial<PlotData>,
  hoveredSegment: number,
  computeHoverText: CriticalProfileChartProps['segmentHoverString']
): Partial<PlotData>[] {
  const { x, y, ...otherProps } = data
  if (!x?.length || !y?.length) {
    return [data]
  }

  const lineColor = (data.marker?.color ??
    GeckoTheme.colors.grayscale[100]) as string
  const [hoverLineColor, hoverFillColor] = deriveHoverColors(lineColor)

  return range(1, x.length).flatMap((_, i) => {
    const pair = [x[i], x[i + 1], y[i], y[i + 1]]
    if (pair.some((v) => v === null || v === undefined)) {
      return []
    }
    return {
      ...otherProps,
      customdata: [i],
      x: x.slice(i, i + 2),
      y: y.slice(i, i + 2),
      mode: 'lines',
      fill: 'tozeroy',
      line: {
        color: hoveredSegment === i ? hoverLineColor : lineColor,
      },
      fillcolor: hoveredSegment === i ? hoverFillColor : COLOR_TRANSPARENT,
      hoverinfo: 'none',
      hovertemplate:
        hoveredSegment === i ? (computeHoverText?.(hoveredSegment) ?? '') : '',
    }
  })
}

export const CriticalProfileChart: FC<CriticalProfileChartProps> = ({
  cdf,
  profiles,
  colors,
  selection,
  showAreaOnHover,
  segmentHoverString,
}) => {
  const coordUnit = useGeckoLocale().getPreferredMeasurementUnit(
    GeckoMeasurementUnit.Meters
  )
  const valueUnit = useGeckoLocale().getPreferredMeasurementUnit(
    cdf?.data.params.measurement_unit ?? GeckoMeasurementUnit.Millimeters
  )
  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        title: { text: `Offset (${coordUnit.name})`, font: PLOTLY_FONT },
        tickfont: PLOTLY_FONT,
      },
      yaxis: {
        title: {
          text: `${cdf?.data.params.view.name ?? 'value'} (${valueUnit.name})`,
          font: PLOTLY_FONT,
        },
        tickfont: PLOTLY_FONT,
      },
      showlegend: false, // Hide the legend
      hovermode: 'x', // Enable hover info when cursor is closest to data
      paper_bgcolor: 'rgba(0, 0, 0, 0)', // Background of the entire graph container
      plot_bgcolor: 'rgba(0, 0, 0, 0)', // Background of the plotting area
      margin: {
        l: 50, // Left margin
        r: 50, // Right margin
        b: 100, // Bottom margin
        t: 10, // Top margin
      },
    }),
    [cdf?.data.params.view.name, coordUnit.name, valueUnit.name]
  )

  const getChartX = useCallback(
    (
      indexPosition: GeckoMeasurement | undefined,
      indexAxis: CriticalProfile['index_axis']
    ) => {
      const regionStart = (
        indexAxis === 'x' ? selection?.x.start : selection?.y.start
      )?.convertTo(indexPosition?.unit ?? coordUnit)
      return regionStart !== undefined && indexPosition !== undefined
        ? GeckoMeasurement.CreateFromValue(
            indexPosition.num - regionStart.num,
            indexPosition.unit
          )
        : undefined
    },
    [coordUnit, selection]
  )

  const data: Partial<PlotData>[] = useMemo(
    () =>
      profiles?.map((profile) => {
        const color =
          colors[profile.id] ?? CantileverTheme.colors.grayscale[100]
        const x: (number | null)[] = []
        const y: (number | null)[] = []
        profile.points.forEach((cpp) => {
          if (cpp.breakBefore && x.length > 0) {
            x.push(null)
            y.push(null)
          }
          x.push(
            getChartX(cpp.indexPosition, profile.index_axis)?.convertTo(
              coordUnit
            ).num ?? null
          )
          y.push(cpp.value?.convertTo(valueUnit).num ?? null)
        })
        return {
          x,
          y,
          mode: 'lines+markers',
          name: profile.name,
          marker: { color },
        }
      }) ?? [],
    [colors, coordUnit, getChartX, profiles, valueUnit]
  )

  const [hoveredSegment, setHoveredSegment] = useState<number>(-1)
  const splitData = useMemo(
    () =>
      data.length === 1 && showAreaOnHover
        ? splitTraceAndAddHover(data[0], hoveredSegment, segmentHoverString)
        : undefined,
    [data, hoveredSegment, segmentHoverString, showAreaOnHover]
  )

  const handleHover = useCallback((event: Readonly<Plotly.PlotHoverEvent>) => {
    const meta = event.points[0].data.customdata?.[0]
    setHoveredSegment(
      typeof meta === 'number' ? meta : event.points[0].curveNumber
    )
  }, [])

  // Event handler for unhover to reset state
  const handleUnhover = useCallback(() => {
    setHoveredSegment(-1)
  }, [])

  return (
    <Plot
      data={splitData ?? data}
      layout={layout}
      config={{
        displayModeBar: false, // This hides the mode bar
      }}
      style={{ width: '100%', height: '250px' }}
      onHover={handleHover}
      onUnhover={handleUnhover}
    />
  )
}
